import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ChangeEvent,
  DragEvent as ReactDragEvent,
  Dispatch,
  KeyboardEvent as ReactKeyboardEvent,
  MutableRefObject,
  SetStateAction,
} from 'react';
import { uploadThemeAsset } from '../api/themeAssetApi';
import type { ThemeAssetKind } from '../api/themeAssetApi';

export interface UploadedLogo {
  /** Preview URL: a saved asset URL or an owned local blob URL. */
  src: string;
  name: string;
  /** Set after a successful S3 upload. null until the upload completes. */
  assetId: number | null;
  /** Local file backing an owned blob preview, retained until replacement or reseeding. */
  file?: File;
}

function revokeOwnedPreview(logo: UploadedLogo | null): void {
  if (logo?.src && logo.file) URL.revokeObjectURL(logo.src);
}

const LOGO_ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif']);
const LOGO_MAX_BYTES = 5 * 1024 * 1024;

function validateLogoFile(file: File): string | null {
  if (!LOGO_ALLOWED_TYPES.has(file.type)) return 'Unsupported file type. Please upload a PNG, JPG, or GIF.';
  if (file.size > LOGO_MAX_BYTES) return 'File is too large. Maximum size is 5MB.';
  return null;
}

export interface LogoUploadHandlers {
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  triggerFilePicker: () => void;
  handleDragOver: (event: ReactDragEvent<HTMLDivElement>) => void;
  handleDragLeave: (event: ReactDragEvent<HTMLDivElement>) => void;
  handleDrop: (event: ReactDragEvent<HTMLDivElement>) => void;
  handleKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  handleDelete: () => void;
}

export interface UseLogoUploadResult {
  logo: UploadedLogo | null;
  setLogo: Dispatch<SetStateAction<UploadedLogo | null>>;
  error: string | null;
  uploading: boolean;
  isDragActive: boolean;
  /** Attach to the hidden file input; triggerFilePicker clicks it. */
  inputRef: MutableRefObject<HTMLInputElement | null>;
  handlers: LogoUploadHandlers;
  /** Revokes and clears the logo, clears the error, and resets drag state. */
  reset: () => void;
}

export interface UseLogoUploadOptions {
  /**
   * externalAccountId for the current scope. Required for S3 upload.
   * When null, assets are uploaded to the base scope.
   * When undefined, no upload occurs (file-only local preview mode).
   */
  scopeAccountId: number | null | undefined;
  kind?: ThemeAssetKind;
  /** Keep a local file preview and let the owning form upload it on submit. */
  stageOnly?: boolean;
  disabled?: boolean;
}

export function useLogoUpload(options: UseLogoUploadOptions = { scopeAccountId: undefined }): UseLogoUploadResult {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [logo, setLogo] = useState<UploadedLogo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  // Per-upload token guards against stale upload completion after scope switch
  // or a replace-before-complete scenario.
  const uploadTokenRef = useRef(0);
  const disabledRef = useRef(Boolean(options.disabled));
  disabledRef.current = Boolean(options.disabled);

  useEffect(() => {
    return () => {
      revokeOwnedPreview(logo);
    };
  }, [logo?.src]);

  const processFile = useCallback(async (file: File) => {
    if (disabledRef.current) return;
    const validationError = validateLogoFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);

    // Create blob preview immediately so the UI updates before the upload finishes
    const blobUrl = URL.createObjectURL(file);
    setLogo((current) => {
      revokeOwnedPreview(current);
      return { src: blobUrl, name: file.name, assetId: null, file };
    });

    // Only upload when scopeAccountId is defined (not undefined)
    if (options.scopeAccountId === undefined || options.stageOnly) return;

    // Mint a new token for this upload attempt
    const token = ++uploadTokenRef.current;
    setUploading(true);

    try {
      const asset = await uploadThemeAsset({
        externalAccountId: options.scopeAccountId,
        kind: options.kind ?? 'logo',
        file,
      });

      // Discard if a newer upload or scope change has happened
      if (uploadTokenRef.current !== token) return;

      setLogo((current) => {
        if (!current) return current;
        return { ...current, assetId: asset.id };
      });
    } catch (uploadError) {
      if (uploadTokenRef.current !== token) return;
      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed. Please try again.');
      // Keep the local preview but mark assetId as null (dirty / unsaved)
    } finally {
      if (uploadTokenRef.current === token) setUploading(false);
    }
  }, [options.scopeAccountId, options.kind, options.stageOnly]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file) void processFile(file);
    },
    [processFile]
  );

  const triggerFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleDragOver = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragActive(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) void processFile(file);
    },
    [processFile]
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        triggerFilePicker();
      }
    },
    [triggerFilePicker]
  );

  const handleDelete = useCallback(() => {
    if (disabledRef.current) return;
    // Invalidate any in-flight upload token so its result is dropped
    uploadTokenRef.current += 1;
    setUploading(false);

    // Only clear local state. The S3 object is deleted server-side when the
    // owning form writes the nulled asset id — upsertAccountTheme reference-guards
    // the delete so a shared/inherited base asset is never removed.
    setLogo((current) => {
      revokeOwnedPreview(current);
      return null;
    });
    setError(null);
  }, []);

  const reset = useCallback(() => {
    uploadTokenRef.current += 1;
    setUploading(false);
    setLogo((current) => {
      revokeOwnedPreview(current);
      return null;
    });
    setError(null);
    setIsDragActive(false);
  }, []);

  const handlers = useMemo<LogoUploadHandlers>(
    () => ({
      handleInputChange,
      triggerFilePicker,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleKeyDown,
      handleDelete,
    }),
    [
      handleInputChange,
      triggerFilePicker,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleKeyDown,
      handleDelete,
    ]
  );

  return { logo, setLogo, error, uploading, isDragActive, inputRef, handlers, reset };
}
